import React from 'react'
import { hydrateRoot } from 'react-dom/client'
import smoothscroll from 'smoothscroll-polyfill'
import config from 'config'
import { IntlProvider } from 'intl'
import { DeviceProvider } from 'device'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { loadableReady } from '@loadable/component'
import { ApolloClientsProvider } from 'apollo-client'
import { createClients } from 'apollo-client/client'
import { SvgProvider } from 'svg-provider'
import BrowserRuntime from 'svg-provider/BrowserRuntime'
import { Router, mergeSearchParams } from 'router'
import 'focus-visible'

import App from 'containers/App/App'
import ErrorBoundary from 'containers/ErrorBoundary/ErrorBoundary'
import dumpData from 'containers/App/components/InitialData/util/dumpData'


smoothscroll.polyfill()

const apolloClients = createClients()
const svgRuntime = new BrowserRuntime()

if (config.runEnv !== 'prod') {
  window.__APOLLO_CLIENTS__ = apolloClients
}

window.dumpData = () => dumpData(apolloClients.default)

const cleanQueryParams = () => {
  // remove ltn param
  const url = mergeSearchParams(window.location.toString(), {
    ltn: undefined,
    magicToken: undefined,
  })

  window.history.replaceState({}, document.title, url)
}

cleanQueryParams()


const Root = () => (
  <ApolloClientsProvider clients={apolloClients}>
    <IntlProvider locale="en">
      <HelmetProvider>
        <DeviceProvider value={window.__DEVICE__}>
          <SvgProvider value={svgRuntime}>
            <ErrorBoundary>
              <Router>
                <Helmet>
                  <title>Scentbird</title>
                </Helmet>
                <App isLightweightSSR={window.lightweightSSR} />
              </Router>
            </ErrorBoundary>
          </SvgProvider>
        </DeviceProvider>
      </HelmetProvider>
    </IntlProvider>
  </ApolloClientsProvider>
)

const render = () => (
  loadableReady().then(() => {
    const hydrate = () => {
      hydrateRoot(document.getElementById('root'), <Root />)
    }

    // ATTN it's required because all scripts have async property
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hydrate)
    }
    else {
      hydrate()
    }
  })
)

// https://developers.google.com/web/updates/2016/04/intersectionobserver
const isIntersectionObserverExist = (
  'IntersectionObserver' in window
  && 'IntersectionObserverEntry' in window
  && 'intersectionRatio' in window.IntersectionObserverEntry.prototype
)

if (isIntersectionObserverExist) {
  // Minimal polyfill for Edge 15's lack of `isIntersecting` - https://github.com/w3c/IntersectionObserver/issues/211
  if (!('isIntersecting' in window.IntersectionObserverEntry.prototype)) {
    Object.defineProperty(
      window.IntersectionObserverEntry.prototype,
      'isIntersecting',
      {
        get() {
          return this.intersectionRatio > 0
        },
      }
    )
  }

  render()
}
else {
  // @ts-ignore
  require.ensure([ 'intersection-observer/polyfill' ], (require) => {
    require('intersection-observer/polyfill')
    render()
  })
}
